import { ReactElement, useState } from 'react'
import { ClickAwayListener, Paper, Popper, Grow, styled } from '@mui/material'
import { IconButton } from '@carto/meridian-ds/components'

const Icon = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'open' })<{ open?: boolean }>(
  ({ open, theme }) => ({
    button: {
      color: theme.palette.common.white,
      width: 'auto',

      '&:hover': {
        backgroundColor: theme.palette.white[12]
      },
      ...(open && {
        backgroundColor: theme.palette.white[12]
      })
    }
  })
)

const Container = styled(Popper, { shouldForwardProp: (prop) => prop !== 'maxWidth' })<{ maxWidth?: number }>(
  ({ maxWidth, theme }) => ({
    maxWidth: maxWidth || theme.spacingValue * 40,
    minWidth: theme.spacing(30)
  })
)

const Content = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  width: '100%',
  marginTop: theme.spacing(0.5)
}))

interface HeaderPopperProps {
  className?: string
  icon: ReactElement
  children?: ReactElement
  open: boolean
  maxWidth?: number
  handleOpen: () => void
  handleClose: () => void
  ariaLabelButton: string
}

function HeaderPopper({ icon, children, open, maxWidth, handleOpen, handleClose, ariaLabelButton }: HeaderPopperProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const handleClick = () => {
    if (open) {
      handleClose()
    } else {
      handleOpen()
    }
  }

  return (
    <div ref={(el) => setAnchorEl(el)}>
      <Icon
        open={open}
        onClick={handleClick}
        className={open ? 'open' : ''}
        aria-label={`${ariaLabelButton ?? 'Panel'} ${open ? 'open' : 'closed'}`}
        icon={icon}
      />
      {anchorEl && (
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <Container
          open={open}
          maxWidth={maxWidth}
          role={undefined}
          placement="bottom-end"
          transition
          anchorEl={anchorEl}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: 'right top'
              }}
            >
              <Content elevation={8}>
                {children && (
                  <ClickAwayListener onClickAway={handleClose}>
                    <span>{children}</span>
                  </ClickAwayListener>
                )}
              </Content>
            </Grow>
          )}
        </Container>
      )}
    </div>
  )
}

export default HeaderPopper
